import {
  getLocalStorageItem,
  parseJwt,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import SearchParams from "simplydo/core/searchParams";

export const auth = (api) => ({
  resumeSession(success, fail) {
    const token = getLocalStorageItem("auth_token");
    if (!token) {
      return fail?.();
    }

    api.token = token;
    const tokenData = parseJwt(token);
    // Times expiry by a thousand to get from milliseconds to seconds
    const expiryDate = tokenData.exp * 1000;
    // Get diff between now, and the expiry
    const daysFromNow = Math.floor(
      (expiryDate - Date.now()) / (1000 * 60 * 60 * 24),
    );
    // If diff is less than 29, we request a new token
    if (daysFromNow >= 29) {
      api.queryClient.invalidateQueries();
      return api.options?.onLogin?.(success, fail);
    }

    this.refreshAuthToken(success, () => {
      api.queryClient.invalidateQueries();

      // On failure we attempt to revert using old token
      api.options?.onLogin?.(success, fail);
    });
  },
  login(loginData, success, fail) {
    const {
      email,
      password,
      otp,
      exchangeToken,
      org,
      challengeResponse,
      utmLink,
      ssoProtectionToken,
      otpRecoveryCode,
    } = loginData;
    const payload: any = {
      email,
      password,
      otp,
      exchangeToken,
      org,
      challengeResponse,
      utmLink,
      ssoProtectionToken,
      otpRecoveryCode,
    };
    const params = new SearchParams(window?.location?.search ?? {});
    const stateParam = params.get("state");
    if (stateParam) {
      payload.state = stateParam;
    }
    api.unauthenticatedRequest(
      "POST",
      "/accounts/login",
      payload,
      (data) => {
        if (data.token) {
          api.token = data.token;
          setLocalStorageItem(api.TOKEN_KEY, data.token);

          api.options?.onLogin?.(() => success(data), fail);
        } else {
          // e.g. when OTP required
          success(data);
        }
      },
      fail,
    );
  },
  loginWithToken(token, success, fail) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/login/magic",
      { token },
      (data) => {
        api.token = data.token;
        setLocalStorageItem(api.TOKEN_KEY, data.token);
        api.queryClient.invalidateQueries();

        api.options?.onLogin?.(() => success(data), fail);
      },
      fail,
    );
  },
  requestLoginEmail(email, success, fail) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/tokens/authorise",
      { email },
      success,
      fail,
    );
  },
  authoriseToken(authenticationToken, securityPhrase, success, fail) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/tokens/authorise",
      { authenticationToken, securityPhrase },
      success,
      fail,
    );
  },
  verifyAccount(organisation, email, challengeResponse, success, fail) {
    api.unauthenticatedRequest(
      "GET",
      `/accounts?org_id=${organisation}&email=${encodeURIComponent(email)}&challenge_response=${challengeResponse}`,
      null,
      success,
      fail,
    );
  },
  register(
    organisation,
    email,
    password,
    firstName,
    lastName,
    challengeResponse,
    agreedTermsVersion,
    utmLink,
    utmChallenge,
    inviteToken,
    customTermsVersion,
    success,
    fail,
  ) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts",
      {
        organisation,
        email,
        password,
        firstName,
        lastName,
        challengeResponse,
        agreedTermsVersion,
        utmLink,
        utmChallenge,
        inviteToken,
        customTermsVersion: customTermsVersion ?? "",
      },
      (data) => {
        api.token = data.token;
        setLocalStorageItem(api.TOKEN_KEY, data.token);
        api.queryClient.invalidateQueries();

        api.options?.onLogin?.(() => success(data), fail);
      },
      fail,
    );
  },
  logout(success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/logout",
      null,
      () => {
        api.token = null;
        api.revertToken = null;
        removeLocalStorageItem(api.TOKEN_KEY);
        removeLocalStorageItem(api.REVERT_TOKEN);

        api.options?.onLogout?.();
        api.queryClient.invalidateQueries();
        success?.();
      },
      fail,
    );
  },
  logoutOthers(success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/logout/others",
      null,
      success,
      fail,
    );
  },
  sendPasswordResetEmail(email, organisation, success, fail) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/password/reset",
      { email, organisation },
      success,
      fail,
    );
  },
  sendPasswordSetupEmail(email, organisation, success, fail) {
    api.unauthenticatedRequest(
      "POST",
      "/accounts/password/setup",
      { email, organisation },
      success,
      fail,
    );
  },
  updatePassword(currentPassword, newPassword, success, fail) {
    api.authenticatedRequest(
      "PUT",
      "/accounts/password",
      { currentPassword, newPassword },
      success,
      fail,
    );
  },
  adminUpdatePassword(userId, data, success, fail) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/${userId}/password`,
      data,
      success,
      fail,
    );
  },
  updatePasswordWithToken(token, newPassword, success, fail) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/password",
      { token, newPassword },
      success,
      fail,
    );
  },
  agreeToTerms(version, customTermsVersion, success, fail) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/terms/agree`,
      { version, customTermsVersion },
      success,
      fail,
    );
  },
  provideEmail(data, success, fail) {
    api.authenticatedRequest("PUT", "/accounts/email/add", data, success, fail);
  },
  requestEmailVerification(token, success, fail) {
    const params = new SearchParams({ token });
    api.maybeAuthenticatedRequest(
      "POST",
      `/accounts/email/verify${params.toSafeString()}`,
      null,
      success,
      fail,
    );
  },
  adminUpdateEmail(userId, data, success, fail) {
    api.authenticatedRequest(
      "PUT",
      `/accounts/${userId}/email`,
      data,
      success,
      fail,
    );
  },
  verifyEmail(token, success, fail) {
    api.unauthenticatedRequest(
      "PUT",
      "/accounts/email/verify",
      { token },
      success,
      fail,
    );
  },
  getApiTokens(success, fail) {
    api.authenticatedRequest(
      "GET",
      "/accounts/api/tokens",
      null,
      success,
      fail,
    );
  },
  deleteApiToken(tokenId, tokenIndex, success, fail) {
    const tokenData: { tokenId?: string; tokenIndex?: string } = {};
    if (tokenId !== undefined) tokenData.tokenId = tokenId;
    if (tokenIndex !== undefined) tokenData.tokenIndex = tokenIndex;
    const params = new SearchParams(tokenData);

    api.authenticatedRequest(
      "DELETE",
      `/accounts/api/tokens?${params.toString()}`,
      null,
      success,
      fail,
    );
  },
  viewExternalApiToken(tokenId, tokenIndex, success, fail) {
    const tokenData: { tokenId?: string; tokenIndex?: string } = {};
    if (tokenId !== undefined) tokenData.tokenId = tokenId;
    if (tokenIndex !== undefined) tokenData.tokenIndex = tokenIndex;
    const params = new SearchParams(tokenData);

    api.authenticatedRequest(
      "GET",
      `/accounts/api/tokens/external?${params.toString()}`,
      null,
      success,
      fail,
    );
  },
  createExternalApiToken(data, success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/api/tokens/external",
      data,
      success,
      fail,
    );
  },
  deleteExternalApiToken(tokenId, tokenIndex, success, fail) {
    const tokenData: { tokenId?: string; tokenIndex?: string } = {};
    if (tokenId !== undefined) tokenData.tokenId = tokenId;
    if (tokenIndex !== undefined) tokenData.tokenIndex = tokenIndex;
    const params = new SearchParams(tokenData);

    api.authenticatedRequest(
      "DELETE",
      `/accounts/api/tokens/external?${params.toString()}`,
      null,
      success,
      fail,
    );
  },
  refreshAuthToken(success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/tokens/refresh",
      null,
      (newToken) => {
        api.token = newToken;
        setLocalStorageItem(api.TOKEN_KEY, newToken);
        api.queryClient.invalidateQueries();
        api.options?.onLogin?.(() => success?.(newToken), fail);
      },
      fail,
    );
  },
  generateOtpUri(success, fail) {
    api.authenticatedRequest("POST", "/accounts/mfa", null, success, fail);
  },
  disableOtp(data, success, fail) {
    api.authenticatedRequest("DELETE", "/accounts/mfa", data, success, fail);
  },
  enableOtp(data, success, fail) {
    api.authenticatedRequest("PUT", "/accounts/mfa", data, success, fail);
  },
  updateEmailAddress(data, success, fail) {
    api.authenticatedRequest("PUT", "/accounts/email", data, success, fail);
  },
  generateExchangeToken(success, fail) {
    api.authenticatedRequest("POST", "/accounts/tokens", null, success, fail);
  },
  addPhoneNumber(number, success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone",
      { number },
      success,
      fail,
    );
  },
  removePhoneNumber(number, success, fail) {
    api.authenticatedRequest(
      "DELETE",
      "/accounts/phone",
      { number },
      success,
      fail,
    );
  },
  verifyPhoneNumber(number, code, success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone/verify",
      { number, code },
      success,
      fail,
    );
  },
  resendPhoneNumberVerification(number, success, fail) {
    api.authenticatedRequest(
      "POST",
      "/accounts/phone/verify/new",
      { number },
      success,
      fail,
    );
  },
  requestWebsocketToken(success, fail) {
    api.authenticatedRequest(
      "GET",
      "/accounts/websocket/token",
      null,
      success,
      fail,
    );
  },
  requestWebdriverToken(path, success, fail) {
    api.authenticatedRequest(
      "GET",
      `/accounts/webdriver/token?path=${encodeURIComponent(path)}`,
      null,
      success,
      fail,
    );
  },
  addFCMPushToken(token, success, fail) {
    return api.authenticatedRequest(
      "POST",
      "/accounts/push/fcm_tokens",
      { token },
      success,
      fail,
    );
  },
  removeFCMPushToken(token, success, fail) {
    return api.authenticatedRequest(
      "DELETE",
      "/accounts/push/fcm_tokens",
      { token },
      success,
      fail,
    );
  },
  reportContent(data, success, fail) {
    api.maybeAuthenticatedRequest(
      "POST",
      "/accounts/reportContent",
      data,
      success,
      fail,
    );
  },
});
